import { OrderStatus } from '@/compiled_proto/com/celertech/orderrouting/api/enums/OrderStatusProto';
import { TimeInForceType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/TimeInForceTypeProto';
import { OrderType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/OrderTypeProto';
import ContextMenu, { ContextMenuItem } from '@/components/common/ContextMenu';
import TabFilters from '@/components/common/TabFilters';
import AmendOcoOrderModal from '@/components/modal/AmendOcoOrderModal';
import AmendOrderModal from '@/components/modal/AmendOrderModal';
import DealHistoryModal from '@/components/modal/DealHistoryModal';
import OrderCancellationModal from '@/components/modal/OrderCancellationModal';
import VirtualizedTable from '@/components/table/VirtualizedTable';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { orderColumns } from '@/helpers/table/orderBlotterTableHelper';
import { BlotterItem } from '@/model/blotters';
import { Modify } from '@/model/common';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { selectBlotterItems } from '@/state/reducers/blotterSlice';
import { selectGeneralSettings } from '@/state/reducers/settingSlice';
import { formatDateTime, formatLocalDateTime } from '@/utils/format';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { getInstrumentFormatter, getUniversalInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useTrueDateFormatter } from '@/utils/hooks/useTrueDateFormatter';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { getAssetName, translatePair } from '@/utils/symbolMapping';
import {
    ExpandedState,
    SortingState,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale, useNumberFormatter } from 'react-aria';
import { MdCancel, MdEdit, MdHistory } from 'react-icons/md';

export interface OrderBlotterRow
    extends Modify<
        BlotterItem,
        {
            strategy: string;
            parentStrategyID: string;
            created: string | 0;
            updated: string | 0;
            instrument: string;
            qty: string;
            pctFilled: string;
            cxlQty: string;
            price: string;
            slippage: string;
            lastPrice: string;
            avgPrice: string;
            stopPrice: string;
        }
    > {}

const validateAmendableOrder = (order?: OrderBlotterRow) => {
    const tif = TimeInForceType[order?.tif || ''] as unknown as TimeInForceType;
    const status = OrderStatus[order?.status || ''] as unknown as OrderStatus;
    const orderType = OrderType[order?.orderType || ''] as unknown as OrderType;
    // if duration is DAY/GTC && if order status is NEW/PART_FILLED

    const amendable =
        [TimeInForceType.DAY, TimeInForceType.GTC].includes(tif) &&
        [OrderStatus.NEW, OrderStatus.PART_FILLED].includes(status) &&
        [OrderType.LIMIT, OrderType.STOP_MARKET, OrderType.STOP_LIMIT].includes(orderType);

    return amendable;
};

const getLinkedOrder = (e: RowEvent, orders: BlotterItem[]) => {
    const ocoOrders = orders.filter((order) => {
        const foundParentStrategy = order.metadata.find(({ key }) => key === 'PARENT_STRATEGY_ID');
        return foundParentStrategy && foundParentStrategy.value === e.original.parentStrategyID;
    });

    const linkedOrder = (ocoOrders?.filter((order) => order.orderId !== e.original.orderId) || [])[0];
    return linkedOrder;
};

type RowEvent = { event: MouseEvent; original: OrderBlotterRow };

export type PositionFilter = 'Show All' | 'Filled' | 'Open' | 'Cancelled' | 'Rejected';

interface OrderBlotterTableProps {
    setCSVData: React.Dispatch<React.SetStateAction<any[]>>;
}

const OrderBlotterTable = (props: OrderBlotterTableProps) => {
    const screen = useScreenSizes();

    const lang = useLanguage();
    const orders = useAppSelector(selectBlotterItems);

    const amendDisclosure = useDisclosure(false);
    const amendOcoDisclosure = useDisclosure(false);
    const historyDisclosure = useDisclosure(false);
    const cancelDisclosure = useDisclosure(false);

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        useContextMenu();

    const [orderId, setOrderId] = useState<string | undefined>();
    const [linkedOrderId, setLinkedOrderId] = useState<string | undefined>();
    const [cancelOrder, setCancelOrder] = useState<BlotterItem | undefined>();
    const [cancelOrder2, setCancelOrder2] = useState<BlotterItem | undefined>();
    const [isAmendable, setIsAmendable] = useState<boolean>(false);
    const [isAmendOco, setIsAmendOco] = useState<boolean>(false);
    const [positionFilter, setPositionFilter] = useState<PositionFilter>('Show All');

    const { order, linkedOrder, tableInstance } = useOrderBlotterTable({
        ...props,
        orderId,
        linkedOrderId,
        positionFilter,
        amendDisclosure,
        amendOcoDisclosure,
        handleOpenContextMenu
    });

    const positionFilters: { label: string; value: PositionFilter }[] = useMemo(() => {
        return [
            { label: lang.blotters.showAll, value: 'Show All' },
            // (Filled and Partially Filled)
            { label: lang.blotters.filled, value: 'Filled' },
            // (New and Partially Filled)
            { label: lang.blotters.open, value: 'Open' },
            // (Canceled)
            { label: lang.blotters.cancelled, value: 'Cancelled' },
            // (Rejected)
            { label: lang.blotters.rejected, value: 'Rejected' }
        ];
    }, [lang]);

    const onAction = useCallback(
        (e: RowEvent, callback: () => void) => {
            const { original } = e;
            let amendable = false;
            if (original.orderId.includes('STRATEGYORDER')) amendable = false;
            else amendable = validateAmendableOrder(original);

            if (original.orderId.includes('STRATEGYORDER')) return;
            else if (original.strategy === 'OCO') {
                const linkedOrder = getLinkedOrder(e, orders);
                if (linkedOrder) {
                    setIsAmendOco(true);
                    setOrderId(original.orderId);
                    setLinkedOrderId(linkedOrder.orderId);
                    setIsAmendable(amendable);
                    callback();
                } else {
                    setIsAmendOco(false);
                    setOrderId(original.orderId);
                    setIsAmendable(amendable);
                    callback();
                }
            } else {
                setIsAmendOco(false);
                setOrderId(original.orderId);
                setLinkedOrderId(undefined);
                setIsAmendable(amendable);
                callback();
            }
        },
        [orders]
    );

    return (
        <Fragment>
            <div className="flex flex-col h-full gap-2">
                <TabFilters.Common
                    filters={positionFilters}
                    selectedFilter={positionFilter}
                    setSelectedFilter={setPositionFilter}
                />
                <div className="flex-1 basis-0 overflow-hidden">
                    <VirtualizedTable
                        tableInstance={tableInstance}
                        onCellContextMenu={(e: RowEvent) => onAction(e, () => handleOpenContextMenu(e.event as any))}
                        onRowDoubleClicked={(e: RowEvent) =>
                            onAction(e, () => {
                                if (isAmendOco) amendOcoDisclosure[1].toggle();
                                else if (isAmendable) amendDisclosure[1].toggle();
                            })
                        }
                        onRowClicked={(e: RowEvent) => {
                            if (screen.mobile || screen.tablet)
                                onAction(e, () => handleOpenContextMenu(e.event as any));
                        }}
                        isLoadingTableContent={false}
                    />
                </div>
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}>
                <ContextMenuItem onClick={historyDisclosure[1].open}>
                    <MdHistory />
                    <span>{lang.blotters.viewOrderHistory}</span>
                </ContextMenuItem>
                <ContextMenuItem
                    onClick={() => {
                        if (isAmendOco) amendOcoDisclosure[1].open();
                        else amendDisclosure[1].open();
                    }}
                    disabled={!isAmendable}>
                    <MdEdit />
                    <span>{lang.blotters.amendOrder}</span>
                </ContextMenuItem>
                <ContextMenuItem
                    mode="secondary"
                    onClick={() => {
                        const order = orders.find((order) => order.orderId === orderId);
                        const order2 = linkedOrderId
                            ? orders.find((order) => order.orderId === linkedOrderId)
                            : undefined;
                        setCancelOrder(order);
                        if (order2) setCancelOrder2(order2);
                        else setCancelOrder2(undefined);
                        cancelDisclosure[1].open();
                    }}
                    disabled={!isAmendable}>
                    <MdCancel />
                    <span>{lang.orderForm.cancelOrder}</span>
                </ContextMenuItem>
            </ContextMenu>
            {order && (
                <>
                    <AmendOrderModal
                        order={order}
                        opened={amendDisclosure[0]}
                        handlers={amendDisclosure[1]}
                        onCancelOrder={(orderToCancel) => {
                            setCancelOrder(orderToCancel);
                            setCancelOrder2(undefined);
                            amendDisclosure[1].close();
                            cancelDisclosure[1].open();
                        }}
                    />
                    {linkedOrder && (
                        <AmendOcoOrderModal
                            listId={order.metadata.find(({ key }) => key === 'PARENT_STRATEGY_ID')?.value || ''}
                            orders={{
                                order1: order.orderId > linkedOrder.orderId ? linkedOrder : order,
                                order2: order.orderId > linkedOrder.orderId ? order : linkedOrder
                            }}
                            duration={order.tif}
                            opened={amendOcoDisclosure[0]}
                            handlers={amendOcoDisclosure[1]}
                            onCancelOrder={(orderToCancel, order2ToCancel) => {
                                setCancelOrder(orderToCancel);
                                setCancelOrder2(order2ToCancel);
                                amendOcoDisclosure[1].close();
                                cancelDisclosure[1].open();
                            }}
                        />
                    )}
                    <DealHistoryModal
                        order={order}
                        linkedOrder={linkedOrder}
                        opened={historyDisclosure[0]}
                        handlers={historyDisclosure[1]}
                    />
                    {cancelOrder && (
                        <OrderCancellationModal
                            order1={cancelOrder}
                            order2={cancelOrder2}
                            opened={cancelDisclosure[0]}
                            handlers={cancelDisclosure[1]}
                        />
                    )}
                </>
            )}
        </Fragment>
    );
};

export default OrderBlotterTable;

export const mapOrderBlotters = (
    order: BlotterItem,
    locale: string,
    formatDate: any,
    formatNumber: any,
    isUTC: boolean
): OrderBlotterRow => {
    const { formatPrice, formatAvgPrice } = getInstrumentFormatter(locale, order.pair);
    const isStopMarket = OrderType[order.orderType] === OrderType.STOP_MARKET;

    let price;
    let strategy = '-';
    let parentStrategyID;
    let tableInstrument = translatePair(order.pair);
    const instrumentConfig = getUniversalInstrument(order.pair);

    if (instrumentConfig?.type === 'Future') tableInstrument = instrumentConfig.gcexSymbol;

    const foundStrategy = order.metadata.find(({ key }) => key === 'ORIGINAL_STRATEGY_ORDER_TYPE');
    const foundParentStrategy = order.metadata.find(({ key }) => key === 'PARENT_STRATEGY_ID');

    if (foundStrategy && foundParentStrategy) {
        strategy = foundStrategy.value;
        parentStrategyID = foundParentStrategy.value;
    }

    if (isStopMarket) price = '-';
    else {
        if (order.price) price = formatPrice(order.price);
        else price = '-';
    }

    const slippage = +(order.slippage || 0);

    return {
        ...order,
        ccy: getAssetName(order.ccy),
        strategy,
        parentStrategyID,
        created:
            order.created &&
            (isUTC ? formatDateTime(formatDate, order.created) : formatLocalDateTime(formatDate, order.created)),
        updated:
            order.updated &&
            (isUTC ? formatDateTime(formatDate, order.updated) : formatLocalDateTime(formatDate, order.updated)),
        instrument: tableInstrument,
        qty: order.qty ? formatNumber.format(order.qty) : '-',
        cumQty: order.cumQty ? formatNumber.format(order.cumQty) : '-',
        leavesQty: order.leavesQty ? formatNumber.format(order.leavesQty) : '-',
        cxlQty: ['CANCELED', 'REJECTED'].includes(order.status) ? formatNumber.format(order.qty - order.cumQty) : '-',
        lastQty: order.lastQty ? formatNumber.format(order.lastQty) : '-',
        price,
        // “Filled Qty”/”Ord Qty”
        pctFilled:
            order.qty > 0
                ? BigNumber(order.cumQty)
                      .dividedBy(order.qty || 1)
                      .multipliedBy(100)
                      .decimalPlaces(0)
                      .toNumber() + '%'
                : '-',
        slippage: slippage ? formatNumber.format(slippage) : '-',
        lastPrice: order.lastPrice ? formatPrice(order.lastPrice) : '-',
        avgPrice: order.avgPrice ? formatAvgPrice(order.avgPrice) : '-',
        stopPrice: order.stopPrice ? formatPrice(order.stopPrice) : '-'
    };
};

interface UseOrderBlotterTableProps extends OrderBlotterTableProps {
    orderId: string | undefined;
    linkedOrderId: string | undefined;
    positionFilter: PositionFilter;
    amendDisclosure: ReturnType<typeof useDisclosure>;
    amendOcoDisclosure: ReturnType<typeof useDisclosure>;
    handleOpenContextMenu: (row: any, x: number, y: number) => void;
}

const useOrderBlotterTable = (props: UseOrderBlotterTableProps) => {
    const { orderId, linkedOrderId, positionFilter, amendDisclosure, amendOcoDisclosure, setCSVData } = props;

    const { locale } = useLocale();
    const orders = useAppSelector(selectBlotterItems);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const generalSettings = useAppSelector(selectGeneralSettings);

    const lang = useLanguage();

    const formatDate = useTrueDateFormatter();
    const formatNumber = useNumberFormatter(convertedQtyFormatterConfig);

    const [sorting, setSorting] = useState<SortingState>([{ id: 'updated', desc: true }]);

    // const [debouncedOrderBlotters, setDebouncedOrderBlotters] = useState<BlotterItem[]>([]);
    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});

    const order = useMemo(() => orders.find((order) => order.orderId === orderId), [orders, orderId]);
    const linkedOrder = useMemo(() => orders.find((order) => order.orderId === linkedOrderId), [orders, linkedOrderId]);

    const memoizedOrder = useMemo(() => order, [order, amendDisclosure[0], amendOcoDisclosure[0]]);
    const memoizedLinkedOrder = useMemo(() => linkedOrder, [linkedOrder, amendDisclosure[0], amendOcoDisclosure[0]]);

    const isUTC = useMemo(() => generalSettings.timezone.value === 'UTC', [generalSettings]);

    const formattedOrders: any = useMemo(() => {
        const currentAccountOrders = orders.filter(
            (order) => order.account === currentAccount || order.account === currentAccount?.slice(0, 36)
        );
        const filteredOrders = currentAccountOrders.filter((order) => {
            if (positionFilter === 'Show All') return true;
            if (positionFilter === 'Filled') {
                const isFilled = [OrderStatus.FILLED, OrderStatus.PART_FILLED].includes(OrderStatus[order.status]);
                const isCancelled = OrderStatus.CANCELED === OrderStatus[order.status] && order.cumQty > 0;

                return isFilled || isCancelled;
            }
            if (positionFilter === 'Open') {
                return [OrderStatus.NEW, OrderStatus.PART_FILLED].includes(OrderStatus[order.status]);
            }
            if (positionFilter === 'Cancelled') {
                // OrderStatus.REJECTED
                return [OrderStatus.CANCELED].includes(OrderStatus[order.status]);
            }
            if (positionFilter === 'Rejected') {
                // OrderStatus.REJECTED
                return [OrderStatus.REJECTED].includes(OrderStatus[order.status]);
            }
            return true;
        });

        const orderBlotters = filteredOrders.map((order) =>
            mapOrderBlotters(order, locale, formatDate, formatNumber, isUTC)
        );
        const groupedOrders = groupRowsByParentStrategyId(orderBlotters);

        return groupedOrders;
    }, [orders, locale, currentAccount, positionFilter, isUTC, generalSettings]);

    const columns = useMemo(() => orderColumns(lang, generalSettings), [lang, generalSettings]);

    const tableInstance = useReactTable({
        data: formattedOrders,
        columns,
        state: { sorting, expanded: expandedRows },
        defaultColumn,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: 'includesString',
        onSortingChange: (sorting) => setSorting(sorting),
        onExpandedChange: setExpandedRows,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    // const [debouncedTableInstance] = useDebouncedValue(tableInstance, 5000);

    useEffect(() => {
        setCSVData(formattedOrders);
    }, [formattedOrders]);

    useEffect(() => {
        tableInstance.toggleAllRowsExpanded();
    }, []);

    // useInterval(
    //     () => {
    //         setDebouncedOrderBlotters(store.getState().);
    //     },
    //     5000,
    //     true
    // );

    return { order: memoizedOrder, linkedOrder: memoizedLinkedOrder, tableInstance, formattedOrders };
};

const groupRowsByParentStrategyId = (rows: OrderBlotterRow[]) => {
    const groupedMap = new Map<string, any>();

    rows.forEach((item) => {
        const { parentStrategyID } = item;

        if (parentStrategyID) {
            const modifiedParentStrategyID = 'OCO:' + parentStrategyID;
            if (groupedMap.has(modifiedParentStrategyID)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const group = groupedMap.get(modifiedParentStrategyID)!;

                group.subRows.push(item);
            } else {
                groupedMap.set(modifiedParentStrategyID, {
                    ...item,
                    orderId: modifiedParentStrategyID,
                    status: '-',
                    orderType: '-',
                    qty: '-',
                    price: '-',
                    lastPrice: '-',
                    lastQty: '-',
                    avgPrice: '-',
                    stopPrice: '-',
                    cumQty: '-',
                    leavesQty: '-',
                    executionType: '-',
                    subRows: [item]
                });
            }
        } else {
            groupedMap.set(item.orderId, item);
        }
    });
    return Array.from(groupedMap.values());
};
